// react_app/src/components/admin/translations/FeatureGroupTree.tsx

import { useMemo } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LanguageTextDto } from '../../../api/translationService';

interface FeatureGroup {
  prefix: string;
  label: string;
  total: number;
  translated: number;
}

interface FeatureGroupTreeProps {
  texts: LanguageTextDto[];
  selectedGroup: string | null;
  onSelectGroup: (prefix: string | null) => void;
}

function deriveGroups(texts: LanguageTextDto[]): FeatureGroup[] {
  const groupMap = new Map<string, { total: number; translated: number }>();

  for (const text of texts) {
    const parts = text.name.split('.');
    // Use first 2 segments, or 3 if the second is "Admin" or "Reader"
    let depth = 2;
    if (parts.length >= 3 && (parts[1] === 'Admin' || parts[1] === 'Reader')) {
      depth = 3;
    }
    const prefix = parts.slice(0, depth).join('.');

    const existing = groupMap.get(prefix) || { total: 0, translated: 0 };
    existing.total++;
    if (text.value && text.value.trim() !== '') {
      existing.translated++;
    }
    groupMap.set(prefix, existing);
  }

  return Array.from(groupMap.entries())
    .map(([prefix, counts]) => ({
      prefix,
      label: prefix,
      total: counts.total,
      translated: counts.translated,
    }))
    .sort((a, b) => a.prefix.localeCompare(b.prefix));
}

export function FeatureGroupTree({ texts, selectedGroup, onSelectGroup }: FeatureGroupTreeProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => deriveGroups(texts), [texts]);

  const totalAll = texts.length;
  const translatedAll = texts.filter((tx) => tx.value && tx.value.trim() !== '').length;

  return (
    <nav className="space-y-0.5" aria-label="Feature groups">
      {/* All option */}
      <button
        onClick={() => onSelectGroup(null)}
        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors min-h-[44px] flex items-center justify-between ${
          selectedGroup === null
            ? 'bg-primary-50 text-primary-700 font-medium'
            : 'text-neutral-700 hover:bg-neutral-50'
        }`}
      >
        <span>{t('App.Admin.Translations.AllGroups')}</span>
        <span className="text-xs text-neutral-400">
          {t('App.Admin.Translations.TranslatedCount', { translated: translatedAll, total: totalAll })}
        </span>
      </button>

      {groups.map((group) => (
        <button
          key={group.prefix}
          onClick={() => onSelectGroup(group.prefix)}
          className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors min-h-[44px] flex items-center justify-between ${
            selectedGroup === group.prefix
              ? 'bg-primary-50 text-primary-700 font-medium'
              : 'text-neutral-700 hover:bg-neutral-50'
          }`}
        >
          <span className="font-mono text-xs truncate mr-2">{group.label}</span>
          <span className={`text-xs flex-shrink-0 ${
            group.translated === group.total ? 'text-success-600' : 'text-neutral-400'
          }`}>
            {t('App.Admin.Translations.TranslatedCount', { translated: group.translated, total: group.total })}
          </span>
        </button>
      ))}
    </nav>
  );
}
